import { useEffect, useRef, useCallback, useState } from "react";
import * as BABYLON from "@babylonjs/core";
import { useFloorPlanStore } from "@/stores/floorPlan";
import { useCanvasRegistry } from "@/stores/canvasRegistry";
import { cn } from "@/lib/utils";
import {
  createCabinetMesh,
  toggleCabinetPart,
  disposeCabinetMesh,
  disposeAllCabinetMeshes,
  shouldUseCabinetAnimation,
  getCabinetMeshGroup,
  debugGetCacheKeys,
} from "@/services/cabinetAnimation";
import {
  loadGlbModel,
  createModelInstance,
  disposeModelInstance,
  isModelLoaded,
  clearTemplateCache,
} from "@/services/glbLoader";
import {
  getFloorMaterial,
  getWallMaterial,
  getCeilingMaterial,
  getVolumeMaterial,
  clearMaterialCache,
} from "@/services/pbrMaterials";

// Convert cm to meters for Babylon (1m = 100cm)
const CM_TO_M = 0.01;

// WCAG 2.4.11 Focus Appearance - High contrast highlight color
const FOCUS_HIGHLIGHT_COLOR = BABYLON.Color3.FromHexString("#2563EB"); // Blue-600

// Camera Presets
type CameraPreset = "front" | "corner" | "top" | "eye-level" | "custom";

interface CameraPosition {
  alpha: number;
  beta: number;
  radius: number;
}

const CAMERA_PRESETS: Record<Exclude<CameraPreset, "custom">, { label: string; icon: React.ReactNode; position: CameraPosition }> = {
  front: {
    label: "Front",
    icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    ),
    position: { alpha: 0, beta: Math.PI / 2.2, radius: 6 },
  },
  corner: {
    label: "Corner",
    icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
      </svg>
    ),
    position: { alpha: -Math.PI / 4, beta: Math.PI / 3, radius: 8 },
  },
  top: {
    label: "Top",
    icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ),
    position: { alpha: 0, beta: 0.15, radius: 10 },
  },
  "eye-level": {
    label: "Eye Level",
    icon: (
      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    ),
    position: { alpha: -Math.PI / 6, beta: Math.PI / 2.5, radius: 5 },
  },
};

// Animation duration for camera transitions (ms)
const CAMERA_ANIMATION_DURATION = 500;

export const BabylonScene: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<{
    engine: BABYLON.Engine;
    scene: BABYLON.Scene;
    shadowGenerator: BABYLON.ShadowGenerator | null;
    highlightLayer: BABYLON.HighlightLayer | null;
  } | null>(null);

  // Camera preset state
  const [currentPreset, setCurrentPreset] = useState<CameraPreset>("corner");
  const [customPosition, setCustomPosition] = useState<CameraPosition | null>(null);

  // BYK-4: Track loaded GLB models (set of glbKeys that are loaded)
  const [loadedModels, setLoadedModels] = useState<Set<string>>(new Set());

  const {
    room,
    walls,
    elements,
    volumes,
    selectedId,
    placementMode,
    pendingPlacement,
    showCeiling,
  } = useFloorPlanStore();

  const {
    addVolume,
    moveVolume,
    select,
    getProductById,
    setDraggedProduct,
    setTool,
    confirmClickPlacement,
    toggleCeiling,
  } = useFloorPlanStore();

  // BYK-177: Canvas registry for export
  const registerBabylonRefs = useCanvasRegistry((s) => s.registerBabylonRefs);

  // Animate camera to a preset position
  const animateCameraToPreset = useCallback((preset: Exclude<CameraPreset, "custom">) => {
    if (!sceneRef.current) return;
    const { scene } = sceneRef.current;
    const camera = scene.getCameraByName("camera") as BABYLON.ArcRotateCamera;
    if (!camera) return;

    // Store current position as custom before switching
    if (currentPreset !== "custom") {
      setCustomPosition({
        alpha: camera.alpha,
        beta: camera.beta,
        radius: camera.radius,
      });
    }

    const target = CAMERA_PRESETS[preset].position;
    const frameRate = 60;
    const totalFrames = Math.round((CAMERA_ANIMATION_DURATION / 1000) * frameRate);

    // Alpha animation
    const alphaAnim = new BABYLON.Animation(
      "alphaAnim",
      "alpha",
      frameRate,
      BABYLON.Animation.ANIMATIONTYPE_FLOAT,
      BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
    );
    alphaAnim.setKeys([
      { frame: 0, value: camera.alpha },
      { frame: totalFrames, value: target.alpha },
    ]);

    // Beta animation
    const betaAnim = new BABYLON.Animation(
      "betaAnim",
      "beta",
      frameRate,
      BABYLON.Animation.ANIMATIONTYPE_FLOAT,
      BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
    );
    betaAnim.setKeys([
      { frame: 0, value: camera.beta },
      { frame: totalFrames, value: target.beta },
    ]);

    // Radius animation
    const radiusAnim = new BABYLON.Animation(
      "radiusAnim",
      "radius",
      frameRate,
      BABYLON.Animation.ANIMATIONTYPE_FLOAT,
      BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT
    );
    radiusAnim.setKeys([
      { frame: 0, value: camera.radius },
      { frame: totalFrames, value: target.radius },
    ]);

    // Add easing
    const easingFunction = new BABYLON.CubicEase();
    easingFunction.setEasingMode(BABYLON.EasingFunction.EASINGMODE_EASEINOUT);
    alphaAnim.setEasingFunction(easingFunction);
    betaAnim.setEasingFunction(easingFunction);
    radiusAnim.setEasingFunction(easingFunction);

    camera.animations = [alphaAnim, betaAnim, radiusAnim];
    scene.beginAnimation(camera, 0, totalFrames, false);

    setCurrentPreset(preset);
  }, [currentPreset]);

  // Handle preset button click
  const handlePresetClick = useCallback((preset: Exclude<CameraPreset, "custom">) => {
    animateCameraToPreset(preset);
  }, [animateCameraToPreset]);

  // Restore custom camera position
  const handleCustomClick = useCallback(() => {
    if (!sceneRef.current || !customPosition) return;
    const { scene } = sceneRef.current;
    const camera = scene.getCameraByName("camera") as BABYLON.ArcRotateCamera;
    if (!camera) return;

    const frameRate = 60;
    const totalFrames = Math.round((CAMERA_ANIMATION_DURATION / 1000) * frameRate);

    const alphaAnim = new BABYLON.Animation("alphaAnim", "alpha", frameRate, BABYLON.Animation.ANIMATIONTYPE_FLOAT, BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);
    alphaAnim.setKeys([{ frame: 0, value: camera.alpha }, { frame: totalFrames, value: customPosition.alpha }]);

    const betaAnim = new BABYLON.Animation("betaAnim", "beta", frameRate, BABYLON.Animation.ANIMATIONTYPE_FLOAT, BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);
    betaAnim.setKeys([{ frame: 0, value: camera.beta }, { frame: totalFrames, value: customPosition.beta }]);

    const radiusAnim = new BABYLON.Animation("radiusAnim", "radius", frameRate, BABYLON.Animation.ANIMATIONTYPE_FLOAT, BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);
    radiusAnim.setKeys([{ frame: 0, value: camera.radius }, { frame: totalFrames, value: customPosition.radius }]);

    const easingFunction = new BABYLON.CubicEase();
    easingFunction.setEasingMode(BABYLON.EasingFunction.EASINGMODE_EASEINOUT);
    alphaAnim.setEasingFunction(easingFunction);
    betaAnim.setEasingFunction(easingFunction);
    radiusAnim.setEasingFunction(easingFunction);

    camera.animations = [alphaAnim, betaAnim, radiusAnim];
    scene.beginAnimation(camera, 0, totalFrames, false);

    setCurrentPreset("custom");
  }, [customPosition]);

  // Track when user manually moves camera (becomes "custom")
  useEffect(() => {
    if (!sceneRef.current) return;
    const { scene } = sceneRef.current;
    const camera = scene.getCameraByName("camera") as BABYLON.ArcRotateCamera;
    if (!camera) return;

    const observer = camera.onViewMatrixChangedObservable.add(() => {
      // Only mark as custom if not currently animating
      if (camera.animations.length === 0 && currentPreset !== "custom") {
        setCurrentPreset("custom");
      }
    });

    return () => {
      camera.onViewMatrixChangedObservable.remove(observer);
    };
  }, [currentPreset]);

  // Initialize Babylon engine and scene
  useEffect(() => {
    if (!canvasRef.current) return;

    const engine = new BABYLON.Engine(canvasRef.current, true, {
      preserveDrawingBuffer: true,
      stencil: true,
      antialias: true,
    });

    const scene = new BABYLON.Scene(engine);
    scene.clearColor = new BABYLON.Color4(0.96, 0.97, 0.98, 1);

    // Camera - Arc rotate for orbital view
    const camera = new BABYLON.ArcRotateCamera(
      "camera",
      -Math.PI / 4, // alpha - horizontal rotation
      Math.PI / 3, // beta - vertical angle
      8, // radius - distance from target
      new BABYLON.Vector3(
        (room.width * CM_TO_M) / 2,
        1,
        (room.depth * CM_TO_M) / 2
      ),
      scene
    );
    camera.attachControl(canvasRef.current, true);
    camera.lowerRadiusLimit = 2;
    camera.upperRadiusLimit = 20;
    camera.lowerBetaLimit = 0.1;
    camera.upperBetaLimit = Math.PI / 2 - 0.1;

    // Navigation controls (CAD best practices 2025)
    camera.panningSensibility = 500; // Higher = slower/more controlled pan (was 50)
    camera.wheelPrecision = 3; // Lower = more accurate/faster zoom (was 20)
    camera.zoomToMouseLocation = true; // Zoom to cursor, not center (critical for CAD)
    camera.pinchPrecision = 30; // Touch pinch zoom sensitivity

    // Disable default right-click panning - we'll use spacebar instead
    const pointerInput = camera.inputs.attached.pointers as BABYLON.ArcRotateCameraPointersInput;
    pointerInput.buttons = [0]; // Only left button for orbit (remove right-click pan)

    // Enable inertia for smoother feel
    camera.inertia = 0.9;
    camera.panningInertia = 0.85;

    // Spacebar+drag panning (Figma-style, consistent with 2D canvas)
    let isSpacePressed = false;
    let isPanning = false;
    let lastPointerX = 0;
    let lastPointerY = 0;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === "Space" && !isSpacePressed) {
        isSpacePressed = true;
        if (canvasRef.current) {
          canvasRef.current.style.cursor = "grab";
        }
        e.preventDefault(); // Prevent page scroll
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === "Space") {
        isSpacePressed = false;
        isPanning = false;
        if (canvasRef.current) {
          canvasRef.current.style.cursor = "default";
        }
      }
    };

    const handlePointerDown = (e: PointerEvent) => {
      if (isSpacePressed && e.button === 0) {
        isPanning = true;
        lastPointerX = e.clientX;
        lastPointerY = e.clientY;
        if (canvasRef.current) {
          canvasRef.current.style.cursor = "grabbing";
        }
        e.preventDefault();
      }
    };

    const handlePointerMove = (e: PointerEvent) => {
      if (isPanning && isSpacePressed) {
        const deltaX = e.clientX - lastPointerX;
        const deltaY = e.clientY - lastPointerY;

        // Pan speed factor (adjust for comfortable movement)
        const panSpeed = 0.01 * camera.radius;

        // Calculate pan direction based on camera orientation
        const right = camera.getDirection(BABYLON.Axis.X);
        const up = new BABYLON.Vector3(0, 1, 0);

        // Move camera target (panning)
        camera.target.addInPlace(right.scale(-deltaX * panSpeed));
        camera.target.addInPlace(up.scale(deltaY * panSpeed));

        lastPointerX = e.clientX;
        lastPointerY = e.clientY;
      }
    };

    const handlePointerUp = () => {
      if (isPanning) {
        isPanning = false;
        if (canvasRef.current && isSpacePressed) {
          canvasRef.current.style.cursor = "grab";
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    canvasRef.current.addEventListener("pointerdown", handlePointerDown);
    canvasRef.current.addEventListener("pointermove", handlePointerMove);
    canvasRef.current.addEventListener("pointerup", handlePointerUp);
    canvasRef.current.addEventListener("pointerleave", handlePointerUp);

    // Ambient light
    const ambientLight = new BABYLON.HemisphericLight(
      "ambient",
      new BABYLON.Vector3(0, 1, 0),
      scene
    );
    ambientLight.intensity = 0.6;
    ambientLight.groundColor = new BABYLON.Color3(0.8, 0.8, 0.85);

    // Directional light with shadows
    const dirLight = new BABYLON.DirectionalLight(
      "dirLight",
      new BABYLON.Vector3(-1, -2, -1),
      scene
    );
    dirLight.position = new BABYLON.Vector3(5, 10, 5);
    dirLight.intensity = 0.8;

    // Shadow generator
    const shadowGenerator = new BABYLON.ShadowGenerator(2048, dirLight);
    shadowGenerator.useBlurExponentialShadowMap = true;
    shadowGenerator.blurKernel = 32;
    shadowGenerator.darkness = 0.3;

    // WCAG 2.4.11: HighlightLayer for focus visibility
    const highlightLayer = new BABYLON.HighlightLayer("focusHighlight", scene);
    highlightLayer.outerGlow = true;
    highlightLayer.blurHorizontalSize = 1;
    highlightLayer.blurVerticalSize = 1;

    sceneRef.current = { engine, scene, shadowGenerator, highlightLayer };

    // BYK-177: Register engine/scene refs for export
    registerBabylonRefs({ engine, scene, shadowGenerator, highlightLayer });

    // DEBUG: Log what mesh is picked on pointer down
    scene.onPointerObservable.add((pointerInfo) => {
      if (pointerInfo.type === BABYLON.PointerEventTypes.POINTERDOWN) {
        const pickResult = scene.pick(scene.pointerX, scene.pointerY);
        if (pickResult?.hit && pickResult.pickedMesh) {
          console.log(`[BYK-31 DEBUG] Picked mesh: ${pickResult.pickedMesh.name}`);
        } else {
          console.log(`[BYK-31 DEBUG] No mesh picked`);
        }
      }
    });

    // Render loop
    engine.runRenderLoop(() => {
      scene.render();
    });

    // Handle resize with debounce for performance (BYK-161)
    // Debounce prevents excessive engine.resize() calls during panel dragging
    let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
    const handleResize = () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      resizeTimeout = setTimeout(() => {
        engine.resize();
        resizeTimeout = null;
      }, 16); // ~60fps debounce for smooth resize
    };
    window.addEventListener("resize", handleResize);

    // Store canvas ref for cleanup
    const canvasElement = canvasRef.current;

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      if (canvasElement) {
        canvasElement.removeEventListener("pointerdown", handlePointerDown);
        canvasElement.removeEventListener("pointermove", handlePointerMove);
        canvasElement.removeEventListener("pointerup", handlePointerUp);
        canvasElement.removeEventListener("pointerleave", handlePointerUp);
      }
      // Clear resize timeout on cleanup
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      // BYK-31: Clear cabinet mesh cache before disposing scene
      // This prevents stale references when scene is recreated
      disposeAllCabinetMeshes();
      // BYK-4: Clear GLB template cache
      clearTemplateCache();
      // BYK-4: Clear PBR material cache
      clearMaterialCache();
      // BYK-177: Unregister refs before dispose
      registerBabylonRefs(null);
      scene.dispose();
      engine.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Initial scene setup only; room changes handled in separate effect
  }, []);

  // Update camera target when room changes
  useEffect(() => {
    if (!sceneRef.current) return;
    const { scene } = sceneRef.current;
    const camera = scene.getCameraByName("camera") as BABYLON.ArcRotateCamera;
    if (camera) {
      camera.target = new BABYLON.Vector3(
        (room.width * CM_TO_M) / 2,
        1,
        (room.depth * CM_TO_M) / 2
      );
    }
  }, [room]);

  // BYK-34: Create/update floor and ceiling
  useEffect(() => {
    if (!sceneRef.current) return;
    const { scene } = sceneRef.current;

    // Remove existing floor and ceiling
    scene.getMeshByName("floor")?.dispose();
    scene.getMeshByName("ceiling")?.dispose();

    // Create floor
    const floor = BABYLON.MeshBuilder.CreateGround(
      "floor",
      {
        width: room.width * CM_TO_M,
        height: room.depth * CM_TO_M,
      },
      scene
    );
    floor.position = new BABYLON.Vector3(
      (room.width * CM_TO_M) / 2,
      0,
      (room.depth * CM_TO_M) / 2
    );
    floor.receiveShadows = true;

    // BYK-4: Use PBR material for floor
    floor.material = getFloorMaterial(scene);

    // BYK-34: Create ceiling (inverted ground at room height) - only if showCeiling is true
    if (showCeiling) {
      const ceiling = BABYLON.MeshBuilder.CreateGround(
        "ceiling",
        {
          width: room.width * CM_TO_M,
          height: room.depth * CM_TO_M,
        },
        scene
      );
      ceiling.position = new BABYLON.Vector3(
        (room.width * CM_TO_M) / 2,
        room.height * CM_TO_M,
        (room.depth * CM_TO_M) / 2
      );
      // Flip the ceiling to face downward
      ceiling.rotation.x = Math.PI;

      // BYK-4: Use PBR material for ceiling
      const ceilingMaterial = getCeilingMaterial(scene);
      // Make ceiling semi-transparent to not obscure the view from above
      ceilingMaterial.alpha = 0.4;
      ceilingMaterial.backFaceCulling = false;
      ceiling.material = ceilingMaterial;

      // Ceiling should be visible from both sides when looking from inside
      ceiling.isPickable = false; // Don't interfere with raycasting
    }
  }, [room, showCeiling]);

  // Create/update walls
  useEffect(() => {
    if (!sceneRef.current) return;
    const { scene, shadowGenerator, highlightLayer } = sceneRef.current;

    // Remove existing wall meshes
    scene.meshes.forEach((mesh: BABYLON.AbstractMesh) => {
      if (mesh.name.startsWith("wall-mesh-")) {
        mesh.dispose();
      }
    });

    walls.forEach((wall) => {
      const length =
        Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y) *
        CM_TO_M;
      const height = wall.height * CM_TO_M;
      const thickness = wall.thickness * CM_TO_M;

      const wallMesh = BABYLON.MeshBuilder.CreateBox(
        `wall-mesh-${wall.id}`,
        {
          width: length,
          height: height,
          depth: thickness,
        },
        scene
      );

      // Position at center of wall segment
      const centerX = ((wall.start.x + wall.end.x) / 2) * CM_TO_M;
      const centerZ = ((wall.start.y + wall.end.y) / 2) * CM_TO_M;
      wallMesh.position = new BABYLON.Vector3(centerX, height / 2, centerZ);

      // Rotate to match wall direction
      const angle = Math.atan2(
        wall.end.y - wall.start.y,
        wall.end.x - wall.start.x
      );
      wallMesh.rotation.y = -angle;

      // BYK-4: Use PBR material for walls
      const isSelected = wall.id === selectedId;
      // For walls, we use the shared wall PBR material
      // Selection is handled by the highlight layer
      wallMesh.material = getWallMaterial(scene);

      // WCAG 2.4.11: Add highlight glow to selected wall
      if (isSelected && highlightLayer) {
        highlightLayer.addMesh(wallMesh, FOCUS_HIGHLIGHT_COLOR, true);
      }

      // Shadows
      wallMesh.receiveShadows = true;
      if (shadowGenerator) {
        shadowGenerator.addShadowCaster(wallMesh);
      }

      // Make clickable
      wallMesh.actionManager = new BABYLON.ActionManager(scene);
      wallMesh.actionManager.registerAction(
        new BABYLON.ExecuteCodeAction(
          BABYLON.ActionManager.OnPickTrigger,
          () => select(wall.id)
        )
      );
    });
  }, [walls, selectedId, select]);

  // Create/update structural elements (windows, doors as cutouts/frames)
  useEffect(() => {
    if (!sceneRef.current) return;
    const { scene } = sceneRef.current;

    // Remove existing element meshes
    scene.meshes.forEach((mesh: BABYLON.AbstractMesh) => {
      if (mesh.name.startsWith("element-mesh-")) {
        mesh.dispose();
      }
    });

    elements.forEach((element) => {
      const wall = walls.find((w) => w.id === element.wallId);
      if (!wall) return;

      // Calculate position along wall
      const wallAngle = Math.atan2(
        wall.end.y - wall.start.y,
        wall.end.x - wall.start.x
      );
      const wallLength = Math.hypot(
        wall.end.x - wall.start.x,
        wall.end.y - wall.start.y
      );
      const ratio = element.offset / wallLength;
      const x = (wall.start.x + (wall.end.x - wall.start.x) * ratio) * CM_TO_M;
      const z = (wall.start.y + (wall.end.y - wall.start.y) * ratio) * CM_TO_M;
      const y = (element.elevation + element.height / 2) * CM_TO_M;

      // Create frame representation
      if (element.type === "window") {
        // Window frame
        const frame = BABYLON.MeshBuilder.CreateBox(
          `element-mesh-${element.id}`,
          {
            width: element.width * CM_TO_M,
            height: element.height * CM_TO_M,
            depth: wall.thickness * CM_TO_M + 0.02,
          },
          scene
        );
        frame.position = new BABYLON.Vector3(x, y, z);
        frame.rotation.y = -wallAngle;

        const frameMaterial = new BABYLON.StandardMaterial(
          `frameMat-${element.id}`,
          scene
        );
        frameMaterial.diffuseColor = new BABYLON.Color3(0.6, 0.85, 1);
        frameMaterial.alpha = 0.5;
        frame.material = frameMaterial;
      } else if (element.type === "door" || element.type === "door_double") {
        // Door frame
        const doorHeight = element.height * CM_TO_M;
        const doorWidth = element.width * CM_TO_M;

        const door = BABYLON.MeshBuilder.CreateBox(
          `element-mesh-${element.id}`,
          {
            width: doorWidth,
            height: doorHeight,
            depth: wall.thickness * CM_TO_M + 0.02,
          },
          scene
        );
        door.position = new BABYLON.Vector3(x, doorHeight / 2, z);
        door.rotation.y = -wallAngle;

        const doorMaterial = new BABYLON.StandardMaterial(
          `doorMat-${element.id}`,
          scene
        );
        doorMaterial.diffuseColor = new BABYLON.Color3(0.6, 0.4, 0.2);
        doorMaterial.alpha = 0.8;
        door.material = doorMaterial;
      }
    });
  }, [elements, walls]);

  // BYK-31: Handle cabinet part click (toggle door/drawer)
  const handleCabinetPartClick = useCallback((volumeId: string, partKey: string) => {
    console.log(`[BYK-31] Door/drawer clicked: volume=${volumeId}, part=${partKey}`);
    toggleCabinetPart(volumeId, partKey);
  }, []);

  // BYK-4: Load GLB models for volumes that have glbKey
  useEffect(() => {
    if (!sceneRef.current) return;
    const { scene } = sceneRef.current;

    // Find products with glbKey that aren't loaded yet
    const modelsToLoad: string[] = [];
    for (const vol of volumes) {
      const product = getProductById(vol.productId);
      if (product?.glbKey && !isModelLoaded(product.glbKey) && !loadedModels.has(product.glbKey)) {
        modelsToLoad.push(product.glbKey);
      }
    }

    if (modelsToLoad.length === 0) return;

    // Load models async
    Promise.all(
      modelsToLoad.map(async (glbKey) => {
        const result = await loadGlbModel(glbKey, scene);
        return { glbKey, success: result !== null };
      })
    ).then((results) => {
      // Update loaded models state
      const newLoaded = new Set(loadedModels);
      for (const { glbKey, success } of results) {
        if (success) {
          newLoaded.add(glbKey);
        }
      }
      if (newLoaded.size > loadedModels.size) {
        setLoadedModels(newLoaded);
      }
    });
  }, [volumes, getProductById, loadedModels]);

  // Create/update placed volumes (products)
  useEffect(() => {
    if (!sceneRef.current) return;
    const { scene, shadowGenerator, highlightLayer } = sceneRef.current;

    // Clear highlight layer before updating
    highlightLayer?.removeAllMeshes();

    // Remove existing volume meshes (both simple and cabinet)
    scene.meshes.forEach((mesh: BABYLON.AbstractMesh) => {
      if (mesh.name.startsWith("volume-mesh-")) {
        mesh.dispose();
      }
    });

    // BYK-4: Remove existing GLB instances
    scene.transformNodes.forEach((node: BABYLON.TransformNode) => {
      if (node.name.startsWith("glb-instance-")) {
        const volumeId = node.name.replace("glb-instance-", "");
        // Check if this volume still exists
        const volumeExists = volumes.some((v) => v.id === volumeId);
        if (!volumeExists) {
          disposeModelInstance(volumeId, scene);
        }
      }
    });

    // BYK-31: Track which volume IDs currently exist so we can dispose removed ones
    const currentVolumeIds = new Set(volumes.map((v) => v.id));

    // Dispose cabinets for volumes that no longer exist
    // (But don't dispose cabinets that still exist - this preserves animations!)
    scene.transformNodes.forEach((node: BABYLON.TransformNode) => {
      if (node.name.startsWith("cabinet-root-")) {
        const volumeId = node.name.replace("cabinet-root-", "");
        if (!currentVolumeIds.has(volumeId)) {
          disposeCabinetMesh(volumeId);
          node.dispose();
        }
      }
    });

    volumes.forEach((vol) => {
      const product = getProductById(vol.productId);
      if (!product?.dimensions) return;

      const width = product.dimensions.width * CM_TO_M;
      const height = product.dimensions.height * CM_TO_M;
      const depth = product.dimensions.depth * CM_TO_M;
      const isSelected = vol.id === selectedId;

      // BYK-31: Check if this is a cabinet that should use animated mesh
      // Products have categoryId but we check the product name for cabinet keywords
      const isCabinet = product.name
        ? shouldUseCabinetAnimation(product.name)
        : false;

      console.log(`[BYK-31] Volume ${vol.id}: product.name="${product.name}", isCabinet=${isCabinet}`);

      if (isCabinet) {
        // BYK-31: Check if cabinet already exists to preserve animations
        const cacheKeys = debugGetCacheKeys();
        console.log(`[BYK-31 DEBUG] Cache contains ${cacheKeys.length} cabinets:`, cacheKeys);
        console.log(`[BYK-31 DEBUG] Looking for volume ID: "${vol.id}"`);

        const existingCabinet = getCabinetMeshGroup(vol.id);
        console.log(`[BYK-31 DEBUG] existingCabinet found:`, !!existingCabinet);

        // Validate the cached mesh is still in this scene (not stale from old scene)
        const meshIsValid = existingCabinet?.body?.getScene() === scene;

        // Clear stale cache entry if mesh is from old scene
        if (existingCabinet && !meshIsValid) {
          console.log(`[BYK-31] Cabinet ${vol.id} cache is stale (different scene), will recreate`);
          disposeCabinetMesh(vol.id);
        }

        if (existingCabinet && meshIsValid) {
          // Cabinet exists in current scene - just update highlight, don't recreate
          console.log(`[BYK-31] Cabinet ${vol.id} already exists, updating highlight only`);

          // Update highlight based on selection
          if (highlightLayer) {
            highlightLayer.removeMesh(existingCabinet.body);
            if (isSelected) {
              highlightLayer.addMesh(existingCabinet.body, FOCUS_HIGHLIGHT_COLOR, true);
            }
          }

          // Update position if changed
          existingCabinet.root.position = new BABYLON.Vector3(
            vol.position.x * CM_TO_M,
            height / 2 + vol.position.z * CM_TO_M,
            vol.position.y * CM_TO_M
          );
          existingCabinet.root.rotation.y = ((vol.rotation || 0) * Math.PI) / 180;

          return; // Skip creation
        }

        console.log(`[BYK-31] Creating cabinet mesh for ${vol.id}`);

        // Create multi-part cabinet mesh with door/drawer animations
        const position = new BABYLON.Vector3(
          vol.position.x * CM_TO_M,
          height / 2 + vol.position.z * CM_TO_M,
          vol.position.y * CM_TO_M
        );

        const cabinetGroup = createCabinetMesh({
          volumeId: vol.id,
          width: product.dimensions.width,
          height: product.dimensions.height,
          depth: product.dimensions.depth,
          position,
          rotation: vol.rotation,
          scene,
          productName: product.name || "",
          onPartClick: handleCabinetPartClick,
        });

        // Add highlight to cabinet body when selected
        if (isSelected && highlightLayer) {
          highlightLayer.addMesh(cabinetGroup.body, FOCUS_HIGHLIGHT_COLOR, true);
        }

        // Add shadow casters
        if (shadowGenerator) {
          shadowGenerator.addShadowCaster(cabinetGroup.body);
          cabinetGroup.doors.forEach(({ mesh }) => shadowGenerator.addShadowCaster(mesh));
          cabinetGroup.drawers.forEach(({ mesh }) => shadowGenerator.addShadowCaster(mesh));
        }

        // Enable shadow receiving
        cabinetGroup.body.receiveShadows = true;

        // Add drag behavior to the cabinet body
        const dragBehavior = new BABYLON.PointerDragBehavior({
          dragPlaneNormal: new BABYLON.Vector3(0, 1, 0),
        });
        dragBehavior.useObjectOrientationForDragging = false;

        dragBehavior.onDragStartObservable.add(() => {
          select(vol.id);
        });

        dragBehavior.onDragEndObservable.add(() => {
          moveVolume(vol.id, {
            x: cabinetGroup.root.position.x / CM_TO_M,
            y: cabinetGroup.root.position.z / CM_TO_M,
            z: vol.position.z,
          });
        });

        // Attach drag to root, not body (so doors move with it)
        cabinetGroup.body.addBehavior(dragBehavior);

        // Click on body to select (doors have their own handlers)
        cabinetGroup.body.actionManager = new BABYLON.ActionManager(scene);
        cabinetGroup.body.actionManager.registerAction(
          new BABYLON.ExecuteCodeAction(
            BABYLON.ActionManager.OnPickTrigger,
            () => select(vol.id)
          )
        );
      } else {
        // BYK-4: Check if product has a loaded GLB model
        const glbKey = product.glbKey;
        const hasGlbModel = glbKey && isModelLoaded(glbKey);

        if (hasGlbModel) {
          // Check if instance already exists
          const existingInstance = scene.getTransformNodeByName(`glb-instance-${vol.id}`);
          if (existingInstance) {
            // Update position and rotation
            existingInstance.position = new BABYLON.Vector3(
              vol.position.x * CM_TO_M,
              (product.dimensions.height / 2 + vol.position.z) * CM_TO_M,
              vol.position.y * CM_TO_M
            );
            existingInstance.rotation.y = (vol.rotation * Math.PI) / 180;
            return; // Skip creation
          }

          // Create GLB model instance
          const instanceRoot = createModelInstance(
            glbKey!,
            vol.id,
            vol.position,
            vol.rotation,
            product.dimensions,
            scene
          );

          if (instanceRoot) {
            // Add drag behavior to the root
            const dragBehavior = new BABYLON.PointerDragBehavior({
              dragPlaneNormal: new BABYLON.Vector3(0, 1, 0),
            });
            dragBehavior.useObjectOrientationForDragging = false;

            dragBehavior.onDragStartObservable.add(() => {
              select(vol.id);
            });

            dragBehavior.onDragEndObservable.add(() => {
              moveVolume(vol.id, {
                x: instanceRoot.position.x / CM_TO_M,
                y: instanceRoot.position.z / CM_TO_M,
                z: vol.position.z,
              });
            });

            // Find a mesh in the instance to attach behavior
            const childMeshes = instanceRoot.getChildMeshes();
            if (childMeshes.length > 0) {
              const firstMesh = childMeshes[0] as BABYLON.Mesh;
              firstMesh.addBehavior(dragBehavior);

              // Click to select
              firstMesh.actionManager = new BABYLON.ActionManager(scene);
              firstMesh.actionManager.registerAction(
                new BABYLON.ExecuteCodeAction(
                  BABYLON.ActionManager.OnPickTrigger,
                  () => select(vol.id)
                )
              );

              // Shadows
              childMeshes.forEach((mesh) => {
                mesh.receiveShadows = true;
                if (shadowGenerator && mesh instanceof BABYLON.Mesh) {
                  shadowGenerator.addShadowCaster(mesh);
                }
              });

              // WCAG 2.4.11: Add highlight glow to selected mesh
              if (isSelected && highlightLayer && firstMesh instanceof BABYLON.Mesh) {
                highlightLayer.addMesh(firstMesh, FOCUS_HIGHLIGHT_COLOR, true);
              }
            }

            console.log(`[BYK-4] Created GLB instance for volume ${vol.id}`);
            return; // GLB created successfully
          }
          // Fall through to box mesh if GLB creation failed
        }

        // Create simple box mesh for non-cabinet products (fallback)
        const mesh = BABYLON.MeshBuilder.CreateBox(
          `volume-mesh-${vol.id}`,
          { width, height, depth },
          scene
        );

        mesh.position = new BABYLON.Vector3(
          vol.position.x * CM_TO_M,
          height / 2 + vol.position.z * CM_TO_M,
          vol.position.y * CM_TO_M
        );
        mesh.rotation.y = (vol.rotation * Math.PI) / 180;

        // BYK-4: Use PBR material for volumes
        // Selection is handled by the highlight layer, not material color
        // Use product name to infer surface type (getVolumeMaterial checks for keywords)
        mesh.material = getVolumeMaterial(product.name || undefined, scene, vol.id);

        // WCAG 2.4.11: Add highlight glow to selected mesh
        if (isSelected && highlightLayer) {
          highlightLayer.addMesh(mesh, FOCUS_HIGHLIGHT_COLOR, true);
        }

        // Shadows
        mesh.receiveShadows = true;
        if (shadowGenerator) {
          shadowGenerator.addShadowCaster(mesh);
        }

        // Add drag behavior
        const dragBehavior = new BABYLON.PointerDragBehavior({
          dragPlaneNormal: new BABYLON.Vector3(0, 1, 0),
        });
        dragBehavior.useObjectOrientationForDragging = false;

        dragBehavior.onDragStartObservable.add(() => {
          select(vol.id);
        });

        dragBehavior.onDragEndObservable.add(() => {
          moveVolume(vol.id, {
            x: mesh.position.x / CM_TO_M,
            y: mesh.position.z / CM_TO_M,
            z: vol.position.z,
          });
        });

        mesh.addBehavior(dragBehavior);

        // Click to select
        mesh.actionManager = new BABYLON.ActionManager(scene);
        mesh.actionManager.registerAction(
          new BABYLON.ExecuteCodeAction(
            BABYLON.ActionManager.OnPickTrigger,
            () => select(vol.id)
          )
        );
      }
    });
  }, [volumes, selectedId, getProductById, select, moveVolume, handleCabinetPartClick, loadedModels]);

  // Handle drag and drop from catalog
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const productId = e.dataTransfer.getData("productId");
      if (!productId || !canvasRef.current || !sceneRef.current) return;

      const { scene } = sceneRef.current;

      // Get canvas position
      const rect = canvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Raycast to find ground position
      const pickResult = scene.pick(x, y, (mesh) => mesh.name === "floor");

      if (pickResult?.pickedPoint) {
        addVolume(productId, {
          x: pickResult.pickedPoint.x / CM_TO_M,
          y: pickResult.pickedPoint.z / CM_TO_M,
          z: 0,
        });
      } else {
        // Fallback to center of room
        addVolume(productId, {
          x: room.width / 2,
          y: room.depth / 2,
          z: 0,
        });
      }

      setDraggedProduct(null);
      setTool("select");
    },
    [addVolume, room, setDraggedProduct, setTool]
  );

  // Click on empty space to deselect, or click-to-place product
  useEffect(() => {
    if (!sceneRef.current) return;
    const { scene } = sceneRef.current;

    const handlePointerDown = (evt: BABYLON.PointerInfo) => {
      if (evt.type !== BABYLON.PointerEventTypes.POINTERDOWN) return;

      // Click-to-place mode: raycast to floor and place product
      if (pendingPlacement && placementMode === "click" && evt.pickInfo) {
        // Pick the floor specifically
        const pickResult = scene.pick(
          scene.pointerX,
          scene.pointerY,
          (mesh) => mesh.name === "floor"
        );

        if (pickResult?.pickedPoint) {
          confirmClickPlacement({
            x: pickResult.pickedPoint.x / CM_TO_M,
            y: pickResult.pickedPoint.z / CM_TO_M,
            z: 0,
          });
        }
        return;
      }

      // Normal deselect behavior
      if (evt.pickInfo && !evt.pickInfo.hit) {
        select(null);
      }
    };

    scene.onPointerObservable.add(handlePointerDown);

    return () => {
      scene.onPointerObservable.removeCallback(handlePointerDown);
    };
  }, [select, pendingPlacement, placementMode, confirmClickPlacement]);

  return (
    <div
      className="relative h-full w-full"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <canvas ref={canvasRef} className="h-full w-full" />

      {/* Camera preset buttons */}
      <div className="absolute left-4 top-4 flex flex-col gap-2">
        <div className="rounded-lg bg-white/95 p-1 shadow-lg">
          <div className="mb-1 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-400">
            Camera
          </div>
          {(Object.keys(CAMERA_PRESETS) as Exclude<CameraPreset, "custom">[]).map((preset) => (
            <button
              key={preset}
              onClick={() => handlePresetClick(preset)}
              className={cn(
                "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
                currentPreset === preset
                  ? "bg-blue-100 text-blue-700"
                  : "text-gray-600 hover:bg-gray-100"
              )}
              title={`Switch to ${CAMERA_PRESETS[preset].label} view`}
            >
              {CAMERA_PRESETS[preset].icon}
              <span>{CAMERA_PRESETS[preset].label}</span>
            </button>
          ))}
          {/* Custom position button (only show if we have a saved custom position) */}
          {customPosition && (
            <button
              onClick={handleCustomClick}
              className={cn(
                "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
                currentPreset === "custom"
                  ? "bg-blue-100 text-blue-700"
                  : "text-gray-600 hover:bg-gray-100"
              )}
              title="Return to custom view"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span>Custom</span>
            </button>
          )}
        </div>

        {/* BYK-34: Room visibility controls */}
        <div className="rounded-lg bg-white/95 p-1 shadow-lg">
          <div className="mb-1 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-gray-400">
            Room
          </div>
          <button
            onClick={toggleCeiling}
            className={cn(
              "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
              showCeiling
                ? "bg-blue-100 text-blue-700"
                : "text-gray-600 hover:bg-gray-100"
            )}
            title={showCeiling ? "Hide ceiling" : "Show ceiling"}
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <span>Ceiling</span>
            <span className={cn(
              "ml-auto text-[10px] font-medium",
              showCeiling ? "text-blue-600" : "text-gray-400"
            )}>
              {showCeiling ? "ON" : "OFF"}
            </span>
          </button>
        </div>
      </div>

      {/* View controls hint or placement indicator */}
      <div className="absolute bottom-4 left-4 rounded bg-white/90 px-3 py-1.5 text-xs shadow">
        {pendingPlacement && placementMode === "click" ? (
          <span className="font-medium text-blue-600">Click on floor to place product (ESC to cancel)</span>
        ) : (
          <span className="text-gray-600">
            <span className="font-medium">Controls:</span> Drag to orbit | Scroll to zoom (to cursor) | Space+drag to pan
          </span>
        )}
      </div>

      {/* Room info */}
      <div className="absolute bottom-4 right-4 rounded bg-white/90 px-3 py-1.5 text-xs text-gray-500 shadow">
        {room.width}cm x {room.depth}cm x {room.height}cm
      </div>
    </div>
  );
};

export default BabylonScene;
